/**
 * User location detection utility
 * Detects user's country code using IP-based geolocation
 */

/** Test config interface for location emulator on test pages */
interface PeptalkTestConfig {
  countryCode?: string;
  timezone?: string;
  timezoneOffset?: number;
}

declare global {
  interface Window {
    __PEPTALK_TEST__?: PeptalkTestConfig;
  }
}

export type CountryCode = string;

const LOCATION_CACHE_KEY = 'peptalk_user_country';
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

interface CachedLocation {
  countryCode: CountryCode;
  timestamp: number;
}

interface LocationDetectionResult {
  countryCode: CountryCode;
  source: 'cache' | 'api' | 'default' | 'test';
}

/**
 * Detect user's country code using IP geolocation
 * Uses ipapi.co free tier (no API key required, 1000 requests/day)
 * Falls back to USD if detection fails
 */
export async function detectUserLocation(): Promise<LocationDetectionResult> {
  // TEST MODE: Check for test override (used by location emulator on test pages)
  if (typeof window !== 'undefined' && window.__PEPTALK_TEST__?.countryCode) {
    return {
      countryCode: window.__PEPTALK_TEST__.countryCode,
      source: 'test',
    };
  }

  // Check cache first
  const cached = getCachedLocation();
  if (cached) {
    return {
      countryCode: cached,
      source: 'cache',
    };
  }

  try {
    // Use ipapi.co free API (no key required)
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      signal: AbortSignal.timeout(5000), // 5 second timeout
    });

    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }

    const data = await response.json();
    const countryCode = data.country_code as CountryCode;

    if (!countryCode) {
      throw new Error('No country code in response');
    }

    // Cache the result
    cacheLocation(countryCode);

    return {
      countryCode,
      source: 'api',
    };
  } catch (error) {
    console.warn('[Widget] Location detection failed, using default USD:', error);

    // Return default US for USD currency
    const defaultCountry = 'US';
    cacheLocation(defaultCountry);

    return {
      countryCode: defaultCountry,
      source: 'default',
    };
  }
}

/**
 * Get cached location if still valid
 */
function getCachedLocation(): CountryCode | null {
  try {
    const cached = localStorage.getItem(LOCATION_CACHE_KEY);
    if (!cached) return null;

    const parsed: CachedLocation = JSON.parse(cached);
    const age = Date.now() - parsed.timestamp;

    if (age < CACHE_DURATION_MS) {
      return parsed.countryCode;
    }

    // Cache expired, remove it
    localStorage.removeItem(LOCATION_CACHE_KEY);
    return null;
  } catch {
    // LocalStorage not available or parsing failed
    return null;
  }
}

/**
 * Cache location in localStorage
 */
function cacheLocation(countryCode: CountryCode): void {
  try {
    const cached: CachedLocation = {
      countryCode,
      timestamp: Date.now(),
    };
    localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(cached));
  } catch {
    // LocalStorage not available, silently fail
  }
}
